import { useState } from "react"
import { Link } from "react-router-dom"
import { useSeoMeta } from "@unhead/react"
import {ChevronDown, HelpCircle, ArrowRight, Sparkles, MessageCircleDashed as MessageCircle} from 'lucide-react'
import { calendlyUrl, faqDoula, location, practitionerName, siteName } from "@/data/site"

function FAQ() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  useSeoMeta({
    title: `Foire aux questions (FAQ) · ${siteName}`,
    description: `Toutes les réponses à vos questions sur l'accompagnement doula, les massages et les ateliers à ${location} avec ${practitionerName}.`,
    ogTitle: `FAQ · ${siteName}`,
    ogDescription: `Toutes les réponses à vos questions sur l'accompagnement périnatal et doula avec ${practitionerName}.`,
    twitterCard: "summary_large_image",
  })

  return (
    <div className="text-[#2d221b]">
      {/* En-tête */}
      <section className="mx-auto max-w-4xl px-5 pb-8 pt-14 text-center md:px-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#d6c4b6] bg-[#efe1d4]/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-[#b85c38]">
          <HelpCircle size={14} />
          <span>Foire aux questions</span>
        </div>
        <h1 className="mt-4 font-display text-4xl font-medium leading-tight text-[#2d221b] md:text-5xl">
          Vos questions, <span className="italic text-[#8a4225]">mes réponses</span>
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-[#5e4b3e]">
          Retrouvez ici les réponses aux questions les plus fréquentes sur le rôle de doula, le déroulement des séances, les déplacements et la prise de rendez-vous.
        </p>
      </section>

      {/* Liste des questions / réponses */}
      <section className="mx-auto max-w-3xl px-5 pb-16 md:px-8">
        <div className="space-y-4">
          {faqDoula.map((faq, index) => {
            const isOpen = openFaq === index
            return (
              <div
                key={faq.question}
                className="overflow-hidden rounded-2xl border border-[#dfd0c2] bg-white transition-shadow hover:shadow-warm-xs"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? null : index)}
                  className="flex w-full items-center justify-between gap-4 p-5 text-left font-display text-lg font-medium text-[#2d221b] md:text-xl"
                  aria-expanded={isOpen}
                >
                  <span>{faq.question}</span>
                  <ChevronDown
                    size={20}
                    className={`shrink-0 text-[#b85c38] transition-transform duration-200 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="border-t border-[#f0e6dc] bg-[#faf6f1] p-5 text-sm leading-relaxed text-[#5e4b3e]">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Bloc d'aide supplémentaire */}
        <div className="mt-12 rounded-3xl border border-[#dfd0c2] bg-[#efe1d4]/80 p-8 text-center shadow-warm-xs md:p-10">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">
            <MessageCircle size={16} />
            <span>Vous avez une autre question ?</span>
          </div>
          <h2 className="mt-2 font-display text-2xl font-medium text-[#2d221b] md:text-3xl">
            Échangeons en toute simplicité
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-xs leading-relaxed text-[#5e4b3e] md:text-sm">
            Chaque histoire et chaque famille est unique. Je suis disponible pour répondre à vos interrogations ou pour une rencontre offerte de 15 minutes en visio.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#b85c38] to-[#9d4c2e] px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-white shadow-warm-sm transition-all hover:scale-[1.02] hover:shadow-warm-md"
            >
              <span>Poser ma question</span>
              <ArrowRight size={14} />
            </Link>
            {calendlyUrl ? (
              <a
                href={calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-[#b85c38] bg-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#b85c38] shadow-warm-xs transition-all hover:bg-[#fbf7f2]"
              >
                <Sparkles size={14} />
                <span>Prendre rendez-vous</span>
              </a>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  )
}

export default FAQ
